import type { FunctionComponent, MutableRefObject } from 'react';

import {
  Field,
  Form,
  FormTheme,
  ModalLayoutCenter,
} from '@kanda-libs/ks-component-ts';
import {
  Button,
  Loader,
  SkeletonLoader,
  Text,
} from '@kanda-libs/ks-design-library';
import type {
  FinanceProvider,
  FinanceRate,
} from '@kanda-libs/ks-frontend-services';

import RateRow from './RateRow';
import useCustomRatesForm from './useCustomRatesForm';

export interface CustomRatesModalFormProps {
  handleClose: () => void;
  ref?: MutableRefObject<HTMLElement | undefined>;
  allRates: FinanceRate[];
  existing: FinanceRate[] | null;
  hasExisting: boolean;
  providersRequiringUserId: FinanceProvider[];
  isLoading: boolean;
}

const CustomRatesModalForm: FunctionComponent<CustomRatesModalFormProps> =
  function ({
    handleClose,
    ref,
    allRates,
    existing,
    hasExisting,
    providersRequiringUserId,
    isLoading,
  }) {
    const {
      form,
      step,
      provider,
      providerOptions,
      rows,
      showUserId,
      goNext,
      goBack,
      onSubmit,
      isSubmitting,
    } = useCustomRatesForm(handleClose, {
      allRates,
      existing,
      hasExisting,
      providersRequiringUserId,
    });

    return (
      <>
        <ModalLayoutCenter
          id="staff-company-custom-rates"
          onClose={handleClose}
          ref={ref as MutableRefObject<HTMLDivElement>}
          className="w-10/12 p-4 md:w-160"
        >
          <FormTheme variant="streamline">
            <Form
              id="staff-company-custom-rates"
              form={form}
              onSubmit={onSubmit}
              className="flex w-full flex-col"
            >
              <Text
                text="Custom rates"
                className="mb-4 text-20-24-em text-neutral-900"
              />

              {step === 0 && (
                <>
                  <Text
                    text="Select the lender this company will work with."
                    className="mb-4 text-14-22 text-neutral-600"
                  />
                  <SkeletonLoader
                    isLoading={isLoading}
                    afterLoading={
                      providerOptions.length === 0 ||
                      providerOptions.length === 1 ? (
                        <p>
                          No lender rates available for any of this company's
                          work types - please raise this
                        </p>
                      ) : (
                        <Field.Select
                          name="provider"
                          label="Lender"
                          options={providerOptions}
                        />
                      )
                    }
                  />
                  <Button.Text
                    id="staff-company-custom-rates-next"
                    label="Next"
                    onClick={goNext}
                    disabled={
                      !provider ||
                      providerOptions.length === 0 ||
                      providerOptions.length === 1
                    }
                    className="mx-auto mt-6"
                  />
                </>
              )}

              {step === 1 && (
                <>
                  <Text
                    text="Select the rates to enable for this company and adjust the fee (%) as needed."
                    className="mb-4 text-14-22 text-neutral-600"
                  />
                  {showUserId && (
                    <div className="mb-4">
                      <Field.Validator
                        validation={{
                          required: {
                            value: true,
                            message: 'This lender requires a user id',
                          },
                        }}
                      >
                        <Field.Input
                          name="xid"
                          label="Lender user id"
                          placeholder="Enter the lender-provided user id"
                        />
                      </Field.Validator>
                    </div>
                  )}
                  <div className="flex max-h-96 flex-col overflow-y-auto">
                    {rows.length === 0 ? (
                      <Text
                        text="No rates available for this lender."
                        className="py-4 text-14-22 text-neutral-500"
                      />
                    ) : (
                      rows.map((rate, index) => (
                        <RateRow key={rate.name} index={index} rate={rate} />
                      ))
                    )}
                  </div>
                  <div className="mt-6 flex flex-row justify-between">
                    <Button.Text
                      id="staff-company-custom-rates-back"
                      label="Back"
                      onClick={goBack}
                      variant="outline"
                    />
                    <Button.Text
                      id="staff-company-custom-rates-submit"
                      submit
                      disabled={isSubmitting}
                      label="Save rates"
                    />
                  </div>
                </>
              )}
            </Form>
          </FormTheme>
        </ModalLayoutCenter>
        <Loader isLoading={isSubmitting} />
      </>
    );
  };

export default CustomRatesModalForm;
